// ABOUTME: Goals page wrapper component that integrates SharedAppBar and family data loading
// ABOUTME: Follows the same pattern as DashboardRedesigned for consistent navigation and data management

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import SharedAppBar from './SharedAppBar';
import GoalsManager from './GoalsManager';
import { Family, Child } from '../types/api';
import { familyAPI } from '../services/api';

const GoalsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedFamily, setSelectedFamily] = useState<Family | null>(null);
  const [children, setChildren] = useState<Child[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Only load families data after user is authenticated
    if (user) {
      loadFamilies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadFamilies intentionally omitted; it is stable and adding it would cause extra fetches
  }, [user]);

  useEffect(() => {
    if (selectedFamily) {
      loadChildren(selectedFamily.familyId);
    }
  }, [selectedFamily]);

  const loadFamilies = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const familiesData = await familyAPI.getFamilies();

      // Auto-select first family if available
      if (familiesData.length > 0 && !selectedFamily) {
        setSelectedFamily(familiesData[0]);
      }
    } catch (error) {
      console.error('Failed to load families:', error);
      setError('Unable to load family data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadChildren = async (familyId: number) => {
    try {
      const childrenData = await familyAPI.getChildren(familyId);
      setChildren(childrenData);
    } catch (error) {
      console.error('Failed to load children:', error);
      setError('Unable to load children. Please try again.');
    }
  };

  const handleAppBarMenuClick = (action: string) => {
    switch (action) {
      case 'Dashboard':
        navigate('/');
        break;
      case 'Analytics':
        navigate('/analytics');
        break;
      case 'Event Log':
        navigate('/event-log');
        break;
      case 'My Family':
        navigate('/my-family');
        break;
      case 'Goals':
        // Already on goals page
        break;
      case 'Admin':
        navigate('/admin');
        break;
      case 'My Profile':
        navigate('/my-profile');
        break;
      case 'Logout':
        // Handle logout logic here
        break;
      default:
        // Navigation action not implemented
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <SharedAppBar 
          title="Goals" 
          onMenuClick={handleAppBarMenuClick}
        />
        <Container 
          maxWidth="xl" 
          disableGutters 
          sx={{ 
            flexGrow: 1, 
            pt: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography>Loading goals...</Typography>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <SharedAppBar 
          title="Goals" 
          onMenuClick={handleAppBarMenuClick}
        />
        <Container 
          maxWidth="xl" 
          disableGutters 
          sx={{ 
            flexGrow: 1, 
            pt: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography color="error">{error}</Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <SharedAppBar 
        title="Goals" 
        onMenuClick={handleAppBarMenuClick}
      />
      <Container 
        maxWidth="xl" 
        disableGutters 
        sx={{ 
          flexGrow: 1, 
          pt: 3,
          pb: 1,
          px: { xs: 1, sm: 2 }
        }}
      >
        <GoalsManager
          familyId={selectedFamily?.familyId || 0}
          familyTimezone={selectedFamily?.timezone}
        >
          {children}
        </GoalsManager>
      </Container>
    </Box>
  );
};

export default GoalsPage;